import { useDispatch } from "react-redux";
import Footer from "../../SharedModule/components/Footer";
import {
  hasSuperTimeAdminAccess,
  verifyModuleAccess,
} from "../../SharedModule/utils/accessUtils";
import { RiShareBoxFill } from "react-icons/ri";
import { Spinner } from "../../SharedModule/components/Spinner";
import {
  CLIENTS_ALL_ROLES,
  ROUTE_CLIENTS,
  ROUTE_CLIENTS_PRICE_EXCEPTIONS,
  ROUTE_CLIENTS_RELATIONSHIP_REVIEW,
} from "../../SharedModule/utils/constants";
import { ForbiddenPage } from "../../SharedModule/pages/ForbiddenPage";
import { useLocation, useNavigate, useParams } from "react-router";
import { setActiveTab } from "../../SharedModule/redux/reducers/uiShared.reducer";
import { useEffect } from "react";
import HeaderClientsModule from "../components/HeaderClientsModule";
import { useClientPriceExceptions } from "../hooks/useClientPriceExceptions";
import { NoContent } from "../../SharedModule/components/NoContent";
import image from "./../../styles/legacy/404.png";
import { ClientPriceException } from "../types/clients-module-types";
import {
  dateFormatEntryEdit,
  quantityFormat,
} from "../../SharedModule/utils/formatters";
import { resetScrollY } from "../../SharedModule/utils/pagesUtils";

export const ClientPriceExceptionsPage = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();

  const { id } = useParams();

  // If there is no id, go to clients list
  if (!id) {
    navigate(ROUTE_CLIENTS);
  }

  const {
    isLoadingPriceExceptions,
    clientPriceExceptions,
    userRolesList,
    rolesLoaded,
  } = useClientPriceExceptions(Number(id));

  const timeAdminOrSuperAdmin = hasSuperTimeAdminAccess(userRolesList);

  useEffect(() => {
    document.title = "The Hub - Client Price Exceptions";

    dispatch(setActiveTab(ROUTE_CLIENTS_PRICE_EXCEPTIONS));

    return () => {
      resetScrollY(globalThis.location.pathname);
    };
  }, []);

  const goToManageExceptions = () => {
    let url = process.env.REACT_APP_APPSMITH_CLIENT_PRICE_EXCEPTION_URL;

    const newWindow = window.open(url, "_blank");
    if (newWindow) {
      newWindow.focus();
    }
  };

  return (
    <div className="content">
      <HeaderClientsModule
        isLoadingPage={isLoadingPriceExceptions || !rolesLoaded}
        backFromRelationshipPage={
          location.state?.from === ROUTE_CLIENTS_RELATIONSHIP_REVIEW
        }
      />

      {(isLoadingPriceExceptions || !rolesLoaded) && (
        <Spinner
          style={{
            marginLeft: "50%",
            marginTop: "5%",
          }}
        />
      )}

      {!isLoadingPriceExceptions &&
        rolesLoaded &&
        !verifyModuleAccess(true, CLIENTS_ALL_ROLES, userRolesList) && (
          <ForbiddenPage />
        )}

      {!isLoadingPriceExceptions &&
        rolesLoaded &&
        verifyModuleAccess(true, CLIENTS_ALL_ROLES, userRolesList) && (
          <>
            <div
              className="d-flex justify-content-between"
              style={{ margin: "1% 7%" }}
            >
              <h3 style={{ marginTop: "0.5rem" }}>Price Exceptions</h3>
              {timeAdminOrSuperAdmin && (
                <button
                  className="btn button-secondary"
                  type="button"
                  onClick={() => goToManageExceptions()}
                >
                  Manage Exceptions <RiShareBoxFill />
                </button>
              )}
            </div>

            {(!clientPriceExceptions ||
              clientPriceExceptions?.length === 0) && (
              <NoContent
                title="No Price Exceptions for this Client"
                conditionButton={false}
                image={image}
                style={{ margin: "3% auto" }}
              />
            )}

            {clientPriceExceptions?.length > 0 && (
              <table
                className="table grey-table-header-background time-confirmation"
                style={{ margin: "1% 7%", width: "auto" }}
              >
                <thead className="align-middle sticky-header sticky">
                  <tr>
                    <th
                      className="border-dark border-top fw-500 py-2 ps-4 column-10 border-right"
                      key="type"
                    >
                      Type
                    </th>
                    <th
                      className="border-dark border-top fw-500 py-2 ps-3 column-15"
                      key="name"
                    >
                      Name
                    </th>
                    <th
                      className="border-dark border-top fw-500 py-2 ps-3 text-center column-15"
                      key="regularPrice"
                    >
                      Regular Price
                    </th>
                    <th
                      className="border-dark border-top fw-500 py-2 ps-3 text-center column-15 border-right"
                      key="exceptionPrice"
                    >
                      Exception Price
                    </th>
                    <th
                      className="border-dark border-top fw-500 py-2 ps-3 text-center column-15"
                      key="startDate"
                    >
                      Start Date
                    </th>
                    <th
                      className="border-dark border-top fw-500 py-2 ps-3 text-center column-15 border-right"
                      key="endDate"
                    >
                      End Date
                    </th>
                    <th
                      className="border-dark border-top fw-500 py-2 px-4 text-center column-15"
                      key="status"
                    >
                      Status
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {clientPriceExceptions?.map(
                      (exception: ClientPriceException, index: number) => {
                        return (
                          <tr
                            key={exception.id}
                            style={{ verticalAlign: "middle" }}
                            className={
                              index % 2 === 0
                                ? "even-row-no-border"
                                : "odd-row-no-border"
                            }
                          >
                            <td className="py-0 ps-4 border-dark border-right">
                              {exception.exceptionType}
                            </td>
                            <td className="py-3 ps-3 fw-500 border-dark">
                              <div className="td-text">
                                {exception.exceptionRateName}
                              </div>
                            </td>
                            <td className="py-3 fw-500 text-center border-dark">
                              <div className="td-text">
                                ${quantityFormat(exception.regularRate)}
                              </div>
                            </td>
                            <td className="py-3 fw-500 text-center border-dark border-right">
                              <div className="td-text">
                                ${quantityFormat(exception.exceptionRate)}
                              </div>
                            </td>
                            <td className="py-3 ps-3 fw-500 text-center border-dark">
                              <div className="td-text">
                                {dateFormatEntryEdit(exception.startDate)}
                              </div>
                            </td>
                            <td className="py-3 ps-3 fw-500 text-center border-dark border-right">
                              <div className="td-text">
                                {dateFormatEntryEdit(exception.endDate)}
                              </div>
                            </td>
                            <td className="py-3 px-4 text-center fw-500 border-dark">
                              <div
                                className={`td-status ${exception.status} mx-3`}
                              >
                                {exception.status}
                              </div>
                            </td>
                          </tr>
                        );
                      }
                    )}
                </tbody>
              </table>
            )}
          </>
        )}
      <Footer />
    </div>
  );
};
